// Admin → Invites (spec "Internal testers"): a single-use link that makes whoever signs up
// with it an internal tester: their machine is in the internal region (Jakarta), on the
// founding plan at the invite's size, free for good, with no Plans or Checkout.

import { Tag, type TagTone } from "../ui/Tag";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { admin, type Invite } from "../api";
import { fullDate } from "./Shell";
import { Button } from "../ui/Button";
import { Empty } from "../ui/ds";
import { Select } from "../ui/Select";

const SIZES = [
  { value: "standard-s", label: "Standard S" },
  { value: "standard-m", label: "Standard M" },
  { value: "standard-l", label: "Standard L" },
  { value: "standard-xl", label: "Standard XL" },
];

const STATUS: Record<Invite["status"], { label: string; tone?: TagTone }> = {
  active: { label: "not used yet", tone: "accent" },
  claimed: { label: "signed up, email not confirmed yet", tone: "amber" },
  used: { label: "used", tone: "green" },
  expired: { label: "expired", tone: "red" },
  revoked: { label: "revoked", tone: "red" },
};

function Copy({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="btn btn-xs"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        } catch {
          /* clipboard refused: the link is on screen */
        }
      }}
      data-testid="copy-invite"
    >
      {copied ? "Copied ✓" : "Copy link"}
    </button>
  );
}

export function AdminInvites() {
  const [invites, setInvites] = useState<Invite[] | null>(null);
  const [note, setNote] = useState("");
  const [tier, setTier] = useState("standard-m");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<{ url: string; invite: Invite } | null>(
    null,
  );
  const load = useCallback(async () => {
    const r = await admin.invites();
    if (r.ok) setInvites(r.data.invites);
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    const r = await admin.createInvite(note.trim(), tier);
    setBusy(false);
    if (!r.ok) return setError(r.error);
    setMade(r.data);
    setNote("");
    void load();
  };
  const size = (t: string) => SIZES.find((s) => s.value === t)?.label ?? t;

  return (
    <section
      className="card"
      id="invites"
      aria-labelledby="invites-title"
      data-testid="admin-invites"
    >
      <div className="card-head">
        <div className="card-title">
          <h2 id="invites-title">Internal testers</h2>
          <span className="count">
            {invites?.filter((i) => i.status === "used").length ?? ""}
          </span>
        </div>
      </div>
      <p className="muted small">
        An invite link signs one person up as an internal tester: a cloud
        machine in Jakarta on the founding plan, free for good, with no Plans or
        payment. It works once, for 14 days.
      </p>
      <form
        className="form code-form invite-form"
        onSubmit={submit}
        data-testid="invite-form"
      >
        <div className="code-form-grid">
          <div className="field">
            <label htmlFor="invite-note">For (a note)</label>
            <input
              id="invite-note"
              placeholder="Who it's for"
              maxLength={200}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="invite-note"
            />
          </div>
          <div className="field">
            <label htmlFor="invite-size">Size</label>
            <Select
              id="invite-size"
              value={tier}
              onChange={setTier}
              testid="invite-size"
              options={SIZES}
            />
          </div>
          <Button
            variant="primary"
            type="submit"
            disabled={busy}
            data-testid="create-invite"
          >
            {busy ? "Making…" : "Make invite link"}
          </Button>
        </div>
        {error && (
          <div className="form-error" role="alert">
            {error}
          </div>
        )}
      </form>
      {made && (
        <div
          className="banner banner-ok"
          role="status"
          data-testid="invite-made"
        >
          <div>
            <strong>{made.invite.note || "Invite"}</strong> ·{" "}
            {size(made.invite.tier)}
            <div className="small muted">
              <code data-testid="invite-url">{made.url}</code>
            </div>
            <div className="small muted">Shown once: copy it now.</div>
          </div>
          <Copy text={made.url} />
        </div>
      )}
      <div className="table-wrap table-cards">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">For</th>
              <th scope="col">Size</th>
              <th scope="col">Status</th>
              <th scope="col">Made</th>
              <th scope="col">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {(invites ?? []).map((i) => (
              <tr key={i.id} data-testid="invite-row" data-status={i.status}>
                <td className="cell-head">
                  <span className="cell-strong">{i.note || "—"}</span>
                  {i.usedBy && <div className="muted small">{i.usedBy}</div>}
                </td>
                <td data-label="Size">{size(i.tier)}</td>
                <td data-label="Status">
                  <Tag tone={STATUS[i.status].tone}>
                    {STATUS[i.status].label}
                  </Tag>
                </td>
                <td className="muted" data-label="Made">
                  {fullDate(i.created)}
                </td>
                <td className="cell-actions">
                  {(i.status === "active" || i.status === "claimed") && (
                    <button
                      className="btn btn-xs btn-ghost btn-ghost-danger"
                      onClick={async () => {
                        const r = await admin.revokeInvite(i.id);
                        if (r.ok) void load();
                      }}
                      data-testid="invite-revoke"
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {invites && !invites.length && (
          <Empty size="md" art="mail" className="table-empty" title="No invites yet" lead="An invite you make above shows up here." />
        )}
        {!invites && (
          <div
            className="skeleton"
            aria-busy="true"
            aria-label="Loading invites"
          />
        )}
      </div>
    </section>
  );
}
